import {
  getAccountResetPwdUrl,
  getAccountUpdatePwdUrl,
  getAccountWelcomeUrl,
  getLabReportFileByIdUrl,
  getLabTestByIdUrl,
  getLabTestBySlugUrl,
  getLabTestFormatListUrl,
  getLabTestListUrl,
  getLabTestTemplateByIdUrl,
  getLabTestTemplateBySlugUrl,
  getMailSendUrl,
  getMedicineBySlugUrl,
  getMedicineFormatListUrl,
  getMedicineListUrl,
  getMessagingSendUrl,
  getNotificationEventUrl,
  getPageByIdUrl,
  getPageBySlugUrl,
  getPostBySlugUrl,
  getPostSummariesUrl,
  getReviewsByPatientIdUrl,
  getReviewsByPracticePageIdUrl,
  getReviewsByProviderPageIdUrl,
  LabReportFile,
  LabTestTemplate,
  LabTestType,
  LabTestTypeMini,
  MedicineType,
  MedicineTypeMini,
  Page,
  PageTypeName,
  Post,
  POST_TYPE_KTM,
  PostType,
  Review
} from "./types";
import {
  AccountResetPwdRequest,
  AccountUpdatePwdRequest,
  AccountWelcomeRequest,
  BaseLabReportFileResponse,
  BaseLabTestListResponse,
  BaseLabTestResponse,
  BaseLabTestTemplateResponse,
  BaseMedicineListResponse,
  BaseMedicineResponse,
  BasePageResponse,
  BasePostResponse,
  BasePostsResponse,
  MailSendRequest,
  MessagingSendRequest,
  NotificationEventRequest,
  ReviewsResponse,
} from "@/libwyze-common/api/types";
import {JSON_OBJECT} from "@/shared/json/helpers";

export class Api {

  private static instance: Api;

  static getInstance(): Api {
    if (!Api.instance) {
      Api.instance = new Api();
    }
    return Api.instance;
  }

  private constructor() {
  }

  private getLinkedPagesResponse(type: PageTypeName, resp: BasePageResponse): any[] {
    switch (type) {
      case PageTypeName.PRACTICE:
        return resp.linkedProviderPages;
      case PageTypeName.PROVIDER:
        return resp.linkedPracticePages;
    }
    return [];
  }

  private async fetchPage(url: string): Promise<Page | undefined> {
    const resp = await (await fetch(url)).json() as BasePageResponse;
    let page: Page;
    if (resp) {
      page = JSON_OBJECT.deserializeObject(resp.page, Page);
      if (page) {
        await page.onAfterItemDeserialized();
        await page.fetchLinkedPages(this.getLinkedPagesResponse(page.type, resp));
      }
    }
    return page;
  }

  async fetchPageBySlug(slug: string): Promise<Page | undefined> {
    return this.fetchPage(getPageBySlugUrl(slug));
  }

  async fetchPageById(id: string): Promise<Page | undefined> {
    return this.fetchPage(getPageByIdUrl(id));
  }

  private async fetchReviews(url: string): Promise<Review[]> {
    const resp = await (await fetch(url)).json() as ReviewsResponse;
    let reviews: Review[] = [];
    if (resp) {
      reviews = JSON_OBJECT.deserializeObjectArray(resp.reviews, Review);
      for (const review of reviews) {
        await review.onAfterItemDeserialized();
      }
    }
    return reviews;
  }

  async fetchReviewsByPracticePageId(practicePageId: string): Promise<Review[]> {
    return this.fetchReviews(getReviewsByPracticePageIdUrl(practicePageId));
  }

  async fetchReviewsByProviderPageId(providerPageId: string): Promise<Review[]> {
    return this.fetchReviews(getReviewsByProviderPageIdUrl(providerPageId));
  }

  async fetchReviewsByPatientId(patientId: string): Promise<Review[]> {
    return this.fetchReviews(getReviewsByPatientIdUrl(patientId));
  }

  async fetchMedicineList(q: string): Promise<MedicineTypeMini[] | undefined> {
    const resp = await (await fetch(getMedicineListUrl(q))).json() as BaseMedicineListResponse;
    let medicines: MedicineTypeMini[];
    if (resp) {
      medicines = resp.medicines;
    }
    return medicines;
  }

  async fetchMedicineFormatList(format: "package"): Promise<MedicineType[] | undefined> {
    const resp = await (await fetch(getMedicineFormatListUrl(format))).json() as BaseMedicineListResponse;
    let medicines: MedicineType[];
    if (resp) {
      medicines = resp.medicines;
    }
    return medicines;
  }

  private async fetchMedicine(url: string): Promise<MedicineType | undefined> {
    const resp = await (await fetch(url)).json() as BaseMedicineResponse;
    let medicine: MedicineType;
    if (resp) {
      medicine = resp.medicine;
    }
    return medicine;
  }

  async fetchMedicineBySlug(slug: string): Promise<MedicineType | undefined> {
    return this.fetchMedicine(getMedicineBySlugUrl(slug));
  }

  async fetchLabTestList(q: string): Promise<LabTestTypeMini[] | undefined> {
    const resp = await (await fetch(getLabTestListUrl(q))).json() as BaseLabTestListResponse;
    let labTests: LabTestTypeMini[];
    if (resp) {
      labTests = resp.labTests;
    }
    return labTests;
  }

  async fetchLabTestFormatList(format: "package" | "popular"): Promise<LabTestType[] | undefined> {
    const resp = await (await fetch(getLabTestFormatListUrl(format))).json() as BaseLabTestListResponse;
    let labTests: LabTestType[];
    if (resp) {
      labTests = resp.labTests;
    }
    return labTests;
  }

  private async fetchLabTest(url: string): Promise<LabTestType | undefined> {
    const resp = await (await fetch(url)).json() as BaseLabTestResponse;
    let labTest: LabTestType;
    if (resp) {
      labTest = resp.labTest;
    }
    return labTest;
  }

  async fetchLabTestBySlug(slug: string): Promise<LabTestType | undefined> {
    return this.fetchLabTest(getLabTestBySlugUrl(slug));
  }

  async fetchLabTestById(id: string): Promise<LabTestType | undefined> {
    return this.fetchLabTest(getLabTestByIdUrl(id));
  }

  private async fetchLabTestTemplate(url: string): Promise<LabTestTemplate | undefined> {
    const resp = await (await fetch(url)).json() as BaseLabTestTemplateResponse;
    let labTestTemplate: LabTestTemplate;
    if (resp) {
      labTestTemplate = JSON_OBJECT.deserializeObject(resp.labTestTemplate, LabTestTemplate);
    }
    return labTestTemplate;
  }

  async fetchLabTestTemplateBySlug(slug: string): Promise<LabTestTemplate | undefined> {
    return this.fetchLabTestTemplate(getLabTestTemplateBySlugUrl(slug));
  }

  async fetchLabTestTemplateById(id: string): Promise<LabTestTemplate | undefined> {
    return this.fetchLabTestTemplate(getLabTestTemplateByIdUrl(id));
  }

  private async fetchLabReportFile(url: string): Promise<LabReportFile | undefined> {
    const resp = await (await fetch(url)).json() as BaseLabReportFileResponse;
    let labReportFile: LabReportFile;
    if (resp?.labReportFile) {
      labReportFile = JSON_OBJECT.deserializeObject(resp.labReportFile, LabReportFile);
    }
    return labReportFile;
  }

  async fetchLabReportFileById(practiceId: string, labOrderId: string): Promise<LabReportFile | undefined> {
    return this.fetchLabReportFile(getLabReportFileByIdUrl(practiceId, labOrderId));
  }

  async fetchPostSummaries(postType: PostType): Promise<Post[]> {
    const subPath = POST_TYPE_KTM.findMetadata(postType)?.subPath;
    if (!subPath) {
      return [];
    }
    const resp = await (await fetch(getPostSummariesUrl(subPath))).json() as BasePostsResponse;
    let posts: Post[];
    if (resp) {
      posts = JSON_OBJECT.deserializeObjectArray(resp.posts, Post);
    }
    return posts;
  }

  async fetchPostBySlug(postType: PostType, slug: string): Promise<Post> {
    const subPath = POST_TYPE_KTM.findMetadata(postType)?.subPath;
    if (!subPath) {
      return null;
    }
    const resp = await (await fetch(getPostBySlugUrl(subPath, slug))).json() as BasePostResponse;
    let post: Post = null;
    if (resp) {
      post = JSON_OBJECT.deserializeObject(resp.post, Post);
    }
    return post;
  }

  async sendEmail(request: MailSendRequest): Promise<void> {
    await fetch(getMailSendUrl(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  }

  async sendMessaging(request: MessagingSendRequest): Promise<void> {
    await fetch(getMessagingSendUrl(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  }

  async welcome(request: AccountWelcomeRequest): Promise<void> {
    await fetch(getAccountWelcomeUrl(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  }

  async resetPassword(request: AccountResetPwdRequest): Promise<void> {
    await fetch(getAccountResetPwdUrl(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  }

  async updatePassword(request: AccountUpdatePwdRequest): Promise<void> {
    await fetch(getAccountUpdatePwdUrl(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(request),
    });
  }

  async notifyEvent<T extends NotificationEventRequest<any>>(request: T): Promise<void> {
    try {
      await fetch(getNotificationEventUrl(), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(request),
      });
    } catch (e) {
      console.error(e);
      // Continue.
    }
  }
}